import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import {
  RiBrushLine,
  RiCheckLine,
  RiHeartLine,
  RiLock2Fill,
  RiTicket2Line,
  RiTrophyLine,
  RiUserAddLine,
  RiVipCrown2Line
} from 'react-icons/ri';

import { Button, Heading, Text } from '@components';
import { mergeClasses } from '@utils';

import MemberBenefitsImage from '../assets/MemberBenefits.jpg';
import {
  FRAME_GRADIENT,
  GLOW,
  getFrameGlow,
  sectionHeadingClassName
} from '../theme';

const UNLOCK_STAGGER_MS = 70;

const prefersReducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Member benefits as tiles in the prize doors' style. Guests see every benefit locked;
 * for members the tiles unlock one after another once the section scrolls into view.
 */
export default function MemberBenefits({
  isAuthenticated,
  prizesHref,
  onJoin
}) {
  const sectionRef = useRef(null);
  const tileRefs = useRef([]);
  const [isInView, setIsInView] = useState(prefersReducedMotion);
  const isUnlocked = isAuthenticated && isInView;

  const benefits = [
    {
      Icon: RiTrophyLine,
      title: t`Dream Week prizes`,
      text: t`A new football prize to win every day of Dream Week.`
    },
    {
      Icon: RiBrushLine,
      title: t`Free boot printing`,
      text: t`Name, number, emoji and flag on your boots.`
    },
    {
      Icon: RiTicket2Line,
      title: t`Tickets & giveaways`,
      text: t`Win match tickets and join member events.`
    },
    {
      Icon: RiVipCrown2Line,
      title: t`Member-only products`,
      text: t`Products only members can buy.`
    },
    {
      Icon: RiHeartLine,
      title: t`Wishlists & orders`,
      text: t`Save favourites and follow every order.`
    }
  ];

  useEffect(() => {
    if (isInView || !sectionRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [isInView]);

  // A short glow on each tile as it unlocks, like a prize door opening.
  useEffect(() => {
    if (!isUnlocked || prefersReducedMotion()) return;

    tileRefs.current.forEach((tile, index) =>
      tile?.animate(
        [
          { boxShadow: `0 0 0 0 rgba(${GLOW}, 0)` },
          { boxShadow: `0 0 28px 2px rgba(${GLOW}, 0.75)`, offset: 0.35 },
          { boxShadow: `0 0 0 0 rgba(${GLOW}, 0)` }
        ],
        {
          duration: 900,
          delay: index * UNLOCK_STAGGER_MS + 120,
          easing: 'ease-out'
        }
      )
    );
  }, [isUnlocked]);

  return (
    <section ref={sectionRef} className="container space-y-6 md:space-y-10">
      <div className="max-w-screen-md space-y-2">
        <Heading as="h2" className={sectionHeadingClassName}>
          {t`Member Benefits`}
        </Heading>
        {/* Capped on phones and tablets for a readable measure, one line from lg. */}
        <Text className="max-w-[46ch] leading-relaxed lg:max-w-none">
          {isAuthenticated
            ? t`You're a member, so all of this is already yours.`
            : t`Unisport FC is free to join, and all of this is yours the moment you sign up.`}
        </Text>
      </div>
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4 lg:grid-rows-[210px_210px] lg:gap-4">
        {/* Framed like an open prize door: the same border, corners and glow. */}
        <div
          className="h-[360px] rounded-xl p-[3px] md:col-span-2 lg:col-span-1 lg:row-span-2 lg:h-auto"
          style={{
            backgroundImage: FRAME_GRADIENT,
            boxShadow: getFrameGlow(GLOW)
          }}
        >
          <div className="relative h-full overflow-hidden rounded-[9px] bg-night-rider">
            <img
              src={MemberBenefitsImage}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 flex flex-col items-start justify-end gap-3 bg-gradient-to-t from-black/95 via-black/60 via-45% to-transparent to-75% p-5 text-white">
              <span className="inline-flex h-6 items-center rounded-full bg-white/20 px-2.5 text-xs font-semibold uppercase leading-none tracking-wide">
                Unisport FC
              </span>
              <h3 className="m-0 text-[28px] font-extrabold uppercase leading-none text-white">
                {isAuthenticated ? t`Welcome back` : t`Free to join`}
              </h3>
              <Text className="max-w-[34ch] text-sm text-white/80">
                {isAuthenticated
                  ? t`Every benefit here is unlocked for you.`
                  : t`Sign up and unlock every benefit, including today's prize.`}
              </Text>
              {isAuthenticated ? (
                <Button size="md" as="a" href={prizesHref}>
                  <Button.Text>{t`See the prizes`}</Button.Text>
                </Button>
              ) : (
                <Button size="md" onClick={onJoin}>
                  <Button.Icon>
                    <RiUserAddLine />
                  </Button.Icon>
                  <Button.Text>{t`Join Unisport FC`}</Button.Text>
                </Button>
              )}
            </div>
          </div>
        </div>
        {benefits.map(({ Icon, title, text }, index) => (
          <div
            key={title}
            ref={tile => {
              tileRefs.current[index] = tile;
            }}
            className={mergeClasses('rounded-xl p-[3px]', {
              'md:col-span-2': index === 0
            })}
            style={{ backgroundImage: FRAME_GRADIENT }}
          >
            <div className="relative flex h-full items-center gap-3 rounded-[9px] bg-[#161616] p-4 text-white lg:flex-col lg:items-start lg:justify-start lg:gap-3 lg:p-5">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#9C50FE]/20 text-[#CDB0FF] lg:h-11 lg:w-11">
                <Icon className="h-5 w-5" />
              </span>
              <div className="min-w-0 flex-1 lg:flex-none">
                <h4 className="m-0 text-sm font-extrabold uppercase leading-tight text-white lg:text-[17px]">
                  {title}
                </h4>
                <Text className="mt-0.5 text-[13px] leading-snug text-white/70 lg:mt-2 lg:text-sm">
                  {text}
                </Text>
              </div>
              <span className="grid shrink-0 lg:absolute lg:right-5 lg:top-5">
                <LockState
                  isUnlocked={isUnlocked}
                  delay={index * UNLOCK_STAGGER_MS}
                />
              </span>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * The lock and unlocked chips overlap in one grid cell and cross-fade. The label is
 * visible from `lg`; below it the chip is an icon with a screen-reader label.
 */
function LockState({ isUnlocked, delay }) {
  const chipClassName =
    'col-start-1 row-start-1 inline-flex h-7 w-7 items-center justify-center gap-1 rounded-full text-xs font-semibold uppercase leading-none tracking-wide transition-[opacity,transform] duration-300 motion-reduce:transition-none lg:h-6 lg:w-auto lg:px-2.5';

  return (
    <>
      <span
        className={mergeClasses(chipClassName, 'bg-white text-black', {
          'scale-75 opacity-0': isUnlocked
        })}
        style={{ transitionDelay: `${delay}ms` }}
        aria-hidden={isUnlocked}
      >
        <RiLock2Fill className="h-3.5 w-3.5 shrink-0" />
        <span className="sr-only lg:not-sr-only">{t`Members`}</span>
      </span>
      <span
        className={mergeClasses(
          chipClassName,
          'bg-gradient-to-r from-green to-[#E0F65A] text-black',
          { 'scale-75 opacity-0': !isUnlocked }
        )}
        style={{ transitionDelay: `${delay}ms` }}
        aria-hidden={!isUnlocked}
      >
        <RiCheckLine className="h-3.5 w-3.5 shrink-0" />
        <span className="sr-only lg:not-sr-only">{t`Unlocked`}</span>
      </span>
    </>
  );
}

LockState.propTypes = {
  isUnlocked: PropTypes.bool.isRequired,
  delay: PropTypes.number.isRequired
};

MemberBenefits.propTypes = {
  isAuthenticated: PropTypes.bool,
  prizesHref: PropTypes.string.isRequired,
  onJoin: PropTypes.func.isRequired
};

MemberBenefits.defaultProps = {
  isAuthenticated: false
};
